import { useState } from 'react';
import clsx from 'clsx';
import {
  markReparatieKleedAfgeleverd,
  markReparatieKleedKlaar,
  markReparatieKleedOpgehaald,
  unmarkReparatieKleedAfgeleverd,
  unmarkReparatieKleedKlaar,
  unmarkReparatieKleedOpgehaald,
  type RepairItemSummary,
  type ReparatieOpdracht,
} from '../../lib/reparatie';

export type CorrectieSoort =
  | 'markeer-gebracht'
  | 'markeer-klaar'
  | 'markeer-opgehaald'
  | 'undo-gebracht'
  | 'undo-klaar'
  | 'undo-opgehaald';

interface Props {
  soort: CorrectieSoort;
  opdracht: ReparatieOpdracht;
  item: RepairItemSummary;
  onClose: () => void;
  onDone: () => void;
}

const VORIGE_STATUS_KEUZES: Array<{ value: string | null; label: string }> = [
  { value: null, label: 'Nog bij klant (nog niet opgehaald)' },
  { value: 'Klaar voor Voorinspectie', label: 'Klaar voor Voorinspectie' },
  { value: 'Aan het reinigen', label: 'Aan het reinigen' },
  { value: 'Drogen', label: 'Drogen' },
  { value: 'In Nabehandeling', label: 'In Nabehandeling' },
  { value: 'Ingepakt', label: 'Ingepakt' },
];

interface Copy {
  titel: string;
  watGebeurt: string;
  watMoetKloppen: string;
  knop: string;
  gevaarlijk: boolean;
}

function copyVoor(soort: CorrectieSoort, rep: string): Copy {
  switch (soort) {
    case 'markeer-gebracht':
      return {
        titel: 'Kleed handmatig als gebracht markeren',
        watGebeurt: `Het kleed gaat op "Bij ${rep}" en de wasserij-status wordt "In Reparatie". Normaal doet de chauffeur dit in de field-app op de reparateur-stop.`,
        watMoetKloppen: 'Het kleed ligt nu fysiek bij de reparateur. Staat het nog in de wasserij of bij de klant, doe dit dan niet.',
        knop: 'Markeer gebracht',
        gevaarlijk: true,
      };
    case 'markeer-klaar':
      return {
        titel: 'Kleed klaarmelden namens de reparateur',
        watGebeurt: 'Het kleed gaat op "Klaar bij reparateur" en het systeem plant de terug-rit. Normaal doet de reparateur dit zelf in het portaal.',
        watMoetKloppen: 'De reparateur heeft gemeld dat de reparatie af is (mail, telefoon, WhatsApp).',
        knop: 'Markeer klaar',
        gevaarlijk: false,
      };
    case 'markeer-opgehaald':
      return {
        titel: 'Kleed handmatig als opgehaald markeren',
        watGebeurt: 'Het kleed gaat op "Terug in wasserij", de wasserij-status wordt "Drogen" en de reparatieregel gaat mee in de aflevering. Normaal doet de chauffeur dit in de field-app.',
        watMoetKloppen: 'Het kleed is fysiek terug in Havelte.',
        knop: 'Markeer opgehaald',
        gevaarlijk: true,
      };
    case 'undo-gebracht':
      return {
        titel: '"Gebracht" terugdraaien',
        watGebeurt: 'De gebracht-stempel gaat eraf, de wasserij-status gaat terug naar wat hij was, en de opdracht valt terug op "Akkoord". Ritten blijven staan.',
        watMoetKloppen: 'Het kleed is in werkelijkheid nooit bij de reparateur afgeleverd (misklik, of te vroeg gestempeld).',
        knop: 'Draai gebracht terug',
        gevaarlijk: true,
      };
    case 'undo-klaar':
      return {
        titel: '"Klaar" terugdraaien',
        watGebeurt: 'De klaar-melding gaat eraf. De automatisch geplande terug-rit voor dit kleed vervalt, tenzij er nog een ander kleed op die rit staat.',
        watMoetKloppen: 'De reparatie is in werkelijkheid nog niet af.',
        knop: 'Draai klaar terug',
        gevaarlijk: false,
      };
    case 'undo-opgehaald':
      return {
        titel: '"Opgehaald" terugdraaien',
        watGebeurt: 'De opgehaald-stempel gaat eraf, het kleed gaat terug naar "In Reparatie" en de reparatieregel gaat weer dicht voor aflevering. Is de order al op "Terugbrengen plannen" geflipt, dan moet dat apart hersteld worden.',
        watMoetKloppen: 'Het kleed ligt in werkelijkheid nog bij de reparateur.',
        knop: 'Draai opgehaald terug',
        gevaarlijk: true,
      };
  }
}

/**
 * Eén dialoog voor alle correcties op de fysieke stempels. Elke correctie
 * vraagt een reden: de stempel is een gebeurtenis, en wie er een verzint of
 * weghaalt hoort dat te kunnen uitleggen. De reden landt in het event.
 */
export function ReparatieCorrectieDialog({ soort, opdracht, item, onClose, onDone }: Props) {
  const rep = opdracht.reparateurNaam ?? 'de reparateur';
  const copy = copyVoor(soort, rep);
  const [reden, setReden] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Alleen bij undo-gebracht als de server de vorige WI-status niet kent.
  const [vraagVorigeStatus, setVraagVorigeStatus] = useState(false);
  const [vorigeStatus, setVorigeStatus] = useState<string | null>(null);

  const redenOk = reden.trim().length >= 5;
  const canSubmit = !busy && redenOk;

  const handleSubmit = async () => {
    if (!canSubmit) return;
    setBusy(true);
    setError(null);
    const r = reden.trim();
    try {
      let res: { success: boolean; message?: string; errorCode?: string };
      switch (soort) {
        case 'markeer-gebracht':
          res = await markReparatieKleedAfgeleverd(item.wiId, { force: true, reden: r });
          break;
        case 'markeer-klaar':
          res = await markReparatieKleedKlaar(item.wiId);
          break;
        case 'markeer-opgehaald':
          res = await markReparatieKleedOpgehaald(item.wiId, { force: true, reden: r });
          break;
        case 'undo-gebracht':
          res = await unmarkReparatieKleedAfgeleverd({
            wiId: item.wiId,
            reden: r,
            ...(vraagVorigeStatus ? { vorigeWiStatus: vorigeStatus } : {}),
          });
          if (!res.success && res.errorCode === 'VORIGE_STATUS_ONBEKEND') {
            setVraagVorigeStatus(true);
            setError('Kies hieronder waar het kleed vóór de stempel was, en bevestig opnieuw.');
            setBusy(false);
            return;
          }
          break;
        case 'undo-klaar':
          res = await unmarkReparatieKleedKlaar(item.wiId);
          break;
        case 'undo-opgehaald':
          res = await unmarkReparatieKleedOpgehaald({ wiId: item.wiId, reden: r });
          break;
      }
      if (!res.success) {
        setError(res.message || 'Mislukt');
        setBusy(false);
        return;
      }
      onDone();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Mislukt');
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/40" onClick={onClose}>
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4" onClick={(e) => e.stopPropagation()}>
        <div className="px-6 py-4 border-b border-slate-200">
          <h2 className="text-base font-semibold text-slate-900">{copy.titel}</h2>
          <p className="text-xs text-slate-500 mt-1">
            {item.wiNaam} · {opdracht.opdrachtNummer}
            {opdracht.reparateurNaam ? ` · ${opdracht.reparateurNaam}` : ''}
          </p>
        </div>
        <div className="px-6 py-4 space-y-3">
          <p className="text-sm text-slate-700">{copy.watGebeurt}</p>
          <div
            className={clsx(
              'text-xs rounded px-2.5 py-2 border',
              copy.gevaarlijk
                ? 'bg-amber-50 border-amber-200 text-amber-900'
                : 'bg-slate-50 border-slate-200 text-slate-700'
            )}
          >
            <span className="font-medium">Dit moet kloppen: </span>
            {copy.watMoetKloppen}
          </div>

          {vraagVorigeStatus && (
            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1">
                Waar was het kleed vóór de stempel?
              </label>
              <select
                className="w-full text-sm border border-slate-300 rounded px-2 py-1.5 bg-white"
                value={vorigeStatus ?? '__null'}
                onChange={(e) => setVorigeStatus(e.target.value === '__null' ? null : e.target.value)}
                disabled={busy}
              >
                {VORIGE_STATUS_KEUZES.map((k) => (
                  <option key={k.label} value={k.value ?? '__null'}>
                    {k.label}
                  </option>
                ))}
              </select>
            </div>
          )}

          <div>
            <label className="block text-xs font-medium text-slate-600 mb-1">
              Reden <span className="text-slate-400 font-normal">(verplicht, minimaal 5 tekens)</span>
            </label>
            <textarea
              rows={2}
              maxLength={255}
              autoFocus
              className="w-full text-sm border border-slate-300 rounded px-2 py-1.5"
              value={reden}
              onChange={(e) => setReden(e.target.value)}
              disabled={busy}
              placeholder={
                soort.startsWith('undo')
                  ? 'Bijv. misklik, kleed ligt nog bij de klant'
                  : 'Bijv. Albert heeft het kleed zelf gebracht op 3 sep'
              }
            />
          </div>
          {error && <p className="text-xs text-red-600">{error}</p>}
        </div>
        <div className="px-6 py-3 border-t border-slate-200 flex items-center justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            disabled={busy}
            className="text-sm text-slate-600 hover:text-slate-900 px-3 py-1.5"
          >
            Terug
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            disabled={!canSubmit}
            className={clsx(
              'text-sm font-medium px-3 py-1.5 rounded',
              !canSubmit
                ? 'bg-slate-200 text-slate-500 cursor-not-allowed'
                : copy.gevaarlijk
                  ? 'bg-amber-600 text-white hover:bg-amber-700'
                  : 'bg-indigo-600 text-white hover:bg-indigo-700'
            )}
          >
            {busy ? 'Bezig…' : copy.knop}
          </button>
        </div>
      </div>
    </div>
  );
}
